import {
  VISITOR_ID_SALT,
  VISITOR_TOKEN_PREFIX,
  sanitizeVisitorName,
  visitorIdFromDigest,
  visitorSecretFromToken,
} from '@orbomb/protocol';

/**
 * A visitor plays online with a name only: no email, no Supabase session.
 * The secret stays on this device and is the whole identity, so clearing
 * site data starts a new visitor. Nothing a visitor plays is ranked.
 */
export interface Visitor {
  /** Seat id on lobby rows, derived from the secret (the match server checks). */
  id: string;
  name: string;
  secret: string;
}

const STORAGE_KEY = 'orbomb.visitor.v1';
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;

function store(): Storage | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    return localStorage;
  } catch {
    return null;
  }
}

export async function visitorIdForSecret(secret: string): Promise<string> {
  const digest = await crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(VISITOR_ID_SALT + secret),
  );
  return visitorIdFromDigest(new Uint8Array(digest));
}

/** 32 random bytes, base64url without padding. */
export function randomVisitorSecret(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/**
 * A new visitor, or `previous` under a new name (same seat id).
 * Null when the name breaks the visitor name rule.
 */
export async function createVisitor(
  name: string,
  previous: Visitor | null = null,
): Promise<Visitor | null> {
  const clean = sanitizeVisitorName(name);
  if (!clean) return null;
  if (previous) return { ...previous, name: clean };
  const secret = randomVisitorSecret();
  return { id: await visitorIdForSecret(secret), name: clean, secret };
}

export function visitorToken(visitor: Visitor): string {
  return `${VISITOR_TOKEN_PREFIX}${visitor.secret}`;
}

export function parseVisitor(value: unknown): Visitor | null {
  if (!value || typeof value !== 'object') return null;
  const raw = value as Partial<Record<keyof Visitor, unknown>>;
  if (typeof raw.id !== 'string' || !UUID.test(raw.id)) return null;
  if (typeof raw.secret !== 'string') return null;
  if (!visitorSecretFromToken(`${VISITOR_TOKEN_PREFIX}${raw.secret}`)) return null;
  const name = typeof raw.name === 'string' ? sanitizeVisitorName(raw.name) : null;
  if (!name) return null;
  return { id: raw.id, name, secret: raw.secret };
}

export function loadVisitor(): Visitor | null {
  const storage = store();
  if (!storage) return null;
  try {
    const text = storage.getItem(STORAGE_KEY);
    return text ? parseVisitor(JSON.parse(text) as unknown) : null;
  } catch {
    return null;
  }
}

export function saveVisitor(visitor: Visitor): void {
  try {
    store()?.setItem(
      STORAGE_KEY,
      JSON.stringify({ id: visitor.id, name: visitor.name, secret: visitor.secret }),
    );
  } catch {
    /* private mode / quota */
  }
}
